import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Plus, Trash2, Users, Check, X } from 'lucide-react';
import Swal from 'sweetalert2';

interface Contact {
  id: string;
  name: string;
  phone_number: string;
  created_at: string;
}

interface ContactManagerProps {
  apiCall: (endpoint: string, method?: string, data?: any) => Promise<any>;
  selectedContacts: string[];
  onContactSelectionChange: (contactIds: string[]) => void;
}

const ContactManager: React.FC<ContactManagerProps> = ({
  apiCall,
  selectedContacts,
  onContactSelectionChange
}) => {
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newContactName, setNewContactName] = useState('');
  const [newContactPhone, setNewContactPhone] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    loadContacts();
  }, []);

  useEffect(() => {
    setSelectAll(selectedContacts.length === contacts.length && contacts.length > 0);
  }, [selectedContacts, contacts]);

  const loadContacts = async () => {
    try {
      const result = await apiCall('/contacts');
      if (result.success) {
        setContacts(result.contacts || []);
      }
    } catch (error) {
      console.error('Error loading contacts:', error);
    }
  };

  const handleAddContact = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newContactName.trim() || !newContactPhone.trim()) return;

    setIsLoading(true);
    try {
      const result = await apiCall('/contacts', 'POST', {
        name: newContactName.trim(),
        phone_number: newContactPhone.trim()
      });

      if (result.success) {
        await loadContacts();
        setNewContactName('');
        setNewContactPhone('');
        setShowAddForm(false);
        Swal.fire({
          icon: 'success',
          title: 'Kontak Ditambahkan',
          text: 'Kontak berhasil ditambahkan',
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (error: any) {
      Swal.fire({
        icon: 'error',
        title: 'Gagal Menambahkan Kontak',
        text: error.message || 'Terjadi kesalahan'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteContact = async (contactId: string, contactName: string) => {
    const result = await Swal.fire({
      title: 'Hapus Kontak',
      text: `Apakah Anda yakin ingin menghapus kontak ${contactName}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Ya, Hapus!',
      cancelButtonText: 'Batal'
    });

    if (!result.isConfirmed) return;

    try {
      const deleteResult = await apiCall(`/contacts/${contactId}`, 'DELETE');
      if (deleteResult.success) {
        await loadContacts();
        // Remove from selected if it was selected
        const updatedSelected = selectedContacts.filter(id => id !== contactId);
        onContactSelectionChange(updatedSelected);
        
        Swal.fire({
          icon: 'success',
          title: 'Kontak Dihapus',
          text: 'Kontak berhasil dihapus',
          timer: 2000,
          showConfirmButton: false
        });
      }
    } catch (error: any) {
      Swal.fire({
        icon: 'error',
        title: 'Gagal Menghapus Kontak',
        text: error.message || 'Terjadi kesalahan'
      });
    }
  };

  const handleContactToggle = (contactId: string) => {
    const isSelected = selectedContacts.includes(contactId);
    let updatedSelected;
    
    if (isSelected) {
      updatedSelected = selectedContacts.filter(id => id !== contactId);
    } else {
      updatedSelected = [...selectedContacts, contactId];
    }
    
    onContactSelectionChange(updatedSelected);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      onContactSelectionChange([]);
    } else {
      onContactSelectionChange(contacts.map(contact => contact.id));
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Users className="h-5 w-5 mr-2 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">Kontak</h2>
            <span className="ml-2 text-sm text-gray-500">({contacts.length})</span>
          </div>
          <Button
            onClick={() => setShowAddForm(!showAddForm)}
            size="sm"
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="h-4 w-4 mr-1" />
            Tambah
          </Button>
        </div>
        
        {contacts.length > 0 && (
          <div className="mt-3 flex items-center space-x-4">
            <Button
              onClick={handleSelectAll}
              variant="outline"
              size="sm"
              className="text-xs"
            >
              {selectAll ? (
                <><X className="h-3 w-3 mr-1" />Batal Pilih Semua</>
              ) : (
                <><Check className="h-3 w-3 mr-1" />Pilih Semua</>
              )}
            </Button>
            {selectedContacts.length > 0 && (
              <span className="text-sm text-blue-600 font-medium">
                {selectedContacts.length} kontak dipilih
              </span>
            )}
          </div>
        )}
      </div>

      {showAddForm && (
        <div className="p-4 border-b border-gray-100 bg-gray-50">
          <form onSubmit={handleAddContact} className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input
                type="text"
                placeholder="Nama kontak"
                value={newContactName}
                onChange={(e) => setNewContactName(e.target.value)}
                required
              />
              <Input
                type="tel"
                placeholder="628123456789"
                value={newContactPhone}
                onChange={(e) => setNewContactPhone(e.target.value)}
                required
              />
            </div>
            <div className="flex space-x-2">
              <Button type="submit" disabled={isLoading} size="sm">
                {isLoading ? 'Menyimpan...' : 'Simpan'}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowAddForm(false);
                  setNewContactName('');
                  setNewContactPhone('');
                }}
              >
                Batal
              </Button>
            </div>
          </form>
        </div>
      )}

      <div className="divide-y divide-gray-100 max-h-64 overflow-y-auto">
        {contacts.length === 0 ? (
          <div className="p-8 text-center">
            <Users className="h-8 w-8 mx-auto mb-3 text-gray-400" />
            <p className="text-gray-500">Belum ada kontak tersimpan</p>
            <p className="text-sm text-gray-400 mt-1">Tambahkan kontak untuk memudahkan broadcast</p>
          </div>
        ) : (
          contacts.map((contact) => {
            const isSelected = selectedContacts.includes(contact.id);
            return (
              <div key={contact.id} className="p-3 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleContactToggle(contact.id)}
                      className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                    <div className="flex-1">
                      <h3 className="font-medium text-gray-900 text-sm">{contact.name}</h3>
                      <p className="text-xs text-gray-500">{contact.phone_number}</p>
                    </div>
                  </div>
                  <Button
                    onClick={() => handleDeleteContact(contact.id, contact.name)}
                    variant="outline"
                    size="sm"
                    className="p-1 h-8 w-8 text-red-600 hover:bg-red-50"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default ContactManager;